import type {
  PayPeriodTemplateCadence,
  PayPeriodTemplateStartDayOfWeek,
} from "@/business/business";

// Two kinds, because they are two different asks: the rules written out, and the numbers
// those rules were applied to. Mirrors PayrollKnowledgeDocument::ONBOARDING_DOCUMENT_TYPES
// on the API, which accepts no other value from this flow.
export const ONBOARDING_DOCUMENT_TYPE = {
  payPlan: "pay_plan",
  referenceData: "reference_data",
} as const;

export type OnboardingDocumentType =
  (typeof ONBOARDING_DOCUMENT_TYPE)[keyof typeof ONBOARDING_DOCUMENT_TYPE];

export interface DocumentExample {
  label: string;
  href: string;
  downloadHref?: string;
}

export interface DocumentCategory {
  title: string;
  stepTitle: string;
  stepSubtitle: string;
  asks: readonly string[];
  examples: readonly DocumentExample[];
}

// Each step saves only the fields it asks about. The API accepts partial updates, so
// every key is optional instead of requiring the full setup at once.
export interface SimpleBusinessSetupPayload {
  name?: string;
  time_zone_label?: string;
  st_credentials_attributes?: {
    tenant_id?: string;
    /** The login the customer made for Volca. */
  };
  current_pay_period_template_attributes?: {
    cadence: PayPeriodTemplateCadence;
    start_day_of_week?: PayPeriodTemplateStartDayOfWeek;
    period_start_reference_date?: string;
  };
}

export interface OnboardingDocument {
  id: string;
  title: string;
  file_name: string;
  uploaded_at: string;
  content_type?: string | null;
  file_size?: number | null;
  // Null for anything sent before onboarding asked which kind it was, and for Volca's own
  // uploads, which use a different vocabulary.
  document_type?: string | null;
  // False for documents Volca staff added: listed, but not the customer's to delete.
  deletable: boolean;
}

export interface OnboardingDocumentsResponse {
  onboarding_documents: OnboardingDocument[];
}

export interface OnboardingDocumentResponse {
  onboarding_document: OnboardingDocument;
}

export const UPLOAD_CONFLICT_RESOLUTION = {
  replace: "replace",
  keepBoth: "keep_both",
} as const;

export type UploadConflictResolution =
  (typeof UPLOAD_CONFLICT_RESOLUTION)[keyof typeof UPLOAD_CONFLICT_RESOLUTION];

// Returned with a 409 when a document of this name already exists, so the screen can ask
// whether to replace it or keep both.
export interface UploadConflict {
  file_name: string;
  suggested_file_name: string;
  existing_document: OnboardingDocument;
}

export const UPLOAD_ITEM_STATE = {
  pending: "pending",
  uploading: "uploading",
  done: "done",
  skipped: "skipped",
  failed: "failed",
} as const;

export type UploadItemState =
  | { state: typeof UPLOAD_ITEM_STATE.pending }
  | { state: typeof UPLOAD_ITEM_STATE.uploading; progress: number }
  | { state: typeof UPLOAD_ITEM_STATE.done }
  | { state: typeof UPLOAD_ITEM_STATE.skipped }
  | { state: typeof UPLOAD_ITEM_STATE.failed; message: string };

export interface UploadItem {
  file: File;
  status: UploadItemState;
}

export type IntakeStep =
  | "welcome"
  | "company"
  | "schedule"
  | "reference_data"
  | "pay_plan"
  | "invoice_sources"
  | "forwarding_setup"
  | "invoice_upload"
  | "st_access"
  | "onboarding_call"
  | "waiting";

// The steps after the invoice source question. They do not depend on each other, so the
// customer picks them from a task row instead of walking them in order.
// Where the customer's invoices arrive today. The email sources bring in the forwarding
// step, the rest bring in the mailbox routing step, and both can apply at once.
// Mirrors BusinessOnboarding::INVOICE_SOURCES on the API.
export const INVOICE_SOURCE = {
  masterInbox: "master_inbox",
  individualMailboxes: "individual_mailboxes",
  vendorPortal: "vendor_portal",
  softwarePortal: "software_portal",
  paper: "paper",
  textMessage: "text_message",
  fax: "fax",
  other: "other",
} as const;

export type InvoiceSource =
  (typeof INVOICE_SOURCE)[keyof typeof INVOICE_SOURCE];

export const EMAIL_INVOICE_SOURCES: readonly InvoiceSource[] = [
  INVOICE_SOURCE.masterInbox,
  INVOICE_SOURCE.individualMailboxes,
];

// How far the ServiceTitan connection has got. Volca generates the API keys and asks
// ServiceTitan for access to the tenant, so the customer's only remaining act is to
// accept that request. Mirrors BusinessOnboardingSerializer#service_titan_stage.
export const SERVICE_TITAN_STAGE = {
  noTenant: "no_tenant",
  awaitingVolca: "awaiting_volca",
  awaitingAcceptance: "awaiting_acceptance",
} as const;

export type ServiceTitanStage =
  (typeof SERVICE_TITAN_STAGE)[keyof typeof SERVICE_TITAN_STAGE];

// Which ServiceTitan user the customer could make for Volca, or that they have none.
export const ST_ACCESS_METHOD = {
  adminUser: "admin_user",
  scopedUser: "scoped_user",
  noServiceTitan: "no_service_titan",
} as const;

export type StAccessMethod =
  (typeof ST_ACCESS_METHOD)[keyof typeof ST_ACCESS_METHOD];

// The stable answer keys. Mirrors BusinessOnboarding::ANSWER_KEYS on the API, which
// accepts no other key.
export interface OnboardingAnswers {
  invoice_sources?: InvoiceSource[];
  invoice_sources_other?: string;
  invoice_sources_summary?: string;
  forwarding_inbox_address?: string;
  portal_invoices_routed?: boolean;
  forwarding_skipped?: boolean;
  st_access_method?: StAccessMethod;
  call_booked?: boolean;
  information_confirmed?: boolean;
}

export interface OnboardingAnswerUpdates
  extends Omit<OnboardingAnswers, "st_access_method"> {
  /** Null withdraws the saved confirmation; an omitted key keeps it. */
  st_access_method?: StAccessMethod | null;
}

// Mirrors Ap::InvoiceSample. Uploaded by the customer, or sent to their Volca address
// while the business is still setting up. Never enters the live invoice pipeline.
export const INVOICE_SAMPLE_SOURCE = {
  upload: "upload",
  email: "email",
} as const;

export type InvoiceSampleSource =
  (typeof INVOICE_SAMPLE_SOURCE)[keyof typeof INVOICE_SAMPLE_SOURCE];

export interface InvoiceSample {
  id: string;
  source: InvoiceSampleSource;
  filename: string;
  content_type: string | null;
  byte_size: number;
  created_at: string;
  url: string;
}

// Mirrors BusinessOnboarding#forwarding_check_state on the API. A probe that has not
// come back is never "failed": the customer's mail rule is somebody else's system, so
// the screen can only say it has not arrived yet.
export const FORWARDING_CHECK_STATE = {
  unsent: "unsent",
  waiting: "waiting",
  confirmed: "confirmed",
  timedOut: "timed_out",
} as const;

export type ForwardingCheckState =
  (typeof FORWARDING_CHECK_STATE)[keyof typeof FORWARDING_CHECK_STATE];

export interface ForwardingCheck {
  state: ForwardingCheckState;
  probe_sent_at: string | null;
  confirmed_at: string | null;
  /** The Volca address the customer points their forwarding rule at, for the check. */
  probe_sender: string;
  /** Gmail sends this to Volca, so the customer can only read it here. */
  verification_code: string | null;
  verification_code_at: string | null;
  /**
   * Every code that reached the address, newest last. Inbound mail carries no usable
   * sender proof, so a message that is not from the customer's provider can land here
   * too. Only the customer can tell which code is theirs.
   *
   * Optional because an api from before this field still answers without it, which is
   * what a browser holds during a rolling deploy.
   */
  verification_codes?: { code: string; at: string }[];
}

export interface BusinessOnboarding {
  id: string;
  business_id: string;
  steps: IntakeStep[];
  current_step: IntakeStep;
  step_progress: Record<
    "shared" | "payroll" | "accounts_payable",
    Partial<Record<IntakeStep, boolean>>
  >;
  answers: OnboardingAnswers;
  submitted_at: string | null;
  setup_state: string;
  ap_inbound_email: string | null;
  invoice_samples: InvoiceSample[];
  forwarding_check: ForwardingCheck;
  service_titan_tenant_id: string | null;
  /** True once Volca holds the ServiceTitan login the customer made for it. */
  service_titan_stage: ServiceTitanStage;
}

export interface OnboardingNavigation {
  step: IntakeStep;
  direction: "next" | "back" | "jump";
  /** The task to move to. Only read for a jump. */
  to?: IntakeStep;
  answers?: OnboardingAnswerUpdates;
  business?: SimpleBusinessSetupPayload;
}
